// Detects whether the cursor sits at a *statement-start* boundary inside a
// procedural body (CREATE PROCEDURE/FUNCTION/TRIGGER … BEGIN … END, a
// PL/pgSQL `$$ … $$` body, a PL/SQL block, a MySQL routine body). At such a
// position the most relevant completions are the dialect's statement-starter
// keywords (SELECT / DECLARE / SET / IF / …) — a bare local variable is not
// even syntactically legal as the first token of a statement — so the
// resolver promotes those keywords to the top of the suggest list.
//
// The check is a cheap backward look at the previous *significant* token
// (the tokenizer already skips comments and whitespace): a statement can
// begin after a `;`, after a block opener (`BEGIN` / `BEGIN TRY` / `BEGIN
// CATCH` / `LOOP` / `THEN` / `ELSE` / `DO` / `AS` / `IS`), or at the very
// start of the body. Mid-expression positions (after an identifier, literal,
// operator, or `=`) return false so the promotion never fires while the user
// is typing a value.

import type { DatabaseEngineId } from '../../../core/engines';
import { tokenize } from './tokenizer';

// Per-dialect keywords whose *last-token* position opens a new statement.
// `;` (semicolon token) and body-start are handled structurally below.
const BOUNDARY_KEYWORDS: Readonly<Record<string, ReadonlySet<string>>> = {
  mssql: new Set(['BEGIN', 'TRY', 'CATCH', 'ELSE', 'AS', 'GO', 'THEN']),
  mysql: new Set(['BEGIN', 'THEN', 'ELSE', 'DO', 'LOOP', 'REPEAT']),
  pgsql: new Set(['BEGIN', 'THEN', 'ELSE', 'LOOP', 'DECLARE']),
  oracle: new Set(['BEGIN', 'THEN', 'ELSE', 'LOOP', 'IS', 'AS', 'DECLARE']),
  sqlite: new Set(['BEGIN', 'THEN', 'ELSE']),
};

export function isAtProceduralStatementStart(
  text: string,
  offset: number,
  engineId: DatabaseEngineId,
): boolean {
  const boundary = BOUNDARY_KEYWORDS[engineId];
  if (!boundary) return false;

  const tokens = tokenize(text, 0, offset, engineId);
  // Find the last token that ends at or before the cursor.
  let last: (typeof tokens)[number] | null = null;
  for (let i = tokens.length - 1; i >= 0; i -= 1) {
    if (tokens[i].end <= offset) {
      last = tokens[i];
      break;
    }
  }
  if (!last) return true; // nothing before the cursor — body start
  if (last.type === 'semicolon') return true;
  if (last.type === 'kw' || last.type === 'ident') {
    return boundary.has(last.text.toUpperCase());
  }
  return false;
}
